// Elio Ribas — IndexedDB schema (Dexie)
// Espejo local de las colecciones del backend, con campos de sync.
import Dexie from 'dexie';

export const db = new Dexie('ElioRibasDB');

db.version(1).stores({
  // Cada tabla expone los campos consultables/indexados.
  // _sync_state: 'synced' | 'pending_create' | 'pending_update' | 'pending_delete' | 'conflict'
  // _local_updated_at: ISO timestamp local
  users: 'id, email, role',
  farms: 'id, name, _sync_state, _local_updated_at',
  lots: 'id, farm_id, name, _sync_state, _local_updated_at',
  animals:
    'id, chip_code, lomo_number, ear_tattoo, farm_id, lot_id, category, status, _sync_state, _local_updated_at',
  events: 'id, animal_id, type, date, _sync_state, _local_updated_at',
  inventories: 'id, farm_id, lot_id, status, started_at, _sync_state, _local_updated_at',
  inventory_scans: 'id, inventory_id, chip_code, animal_id, scanned_at',
  sync_queue: '++id, entity, entity_id, operation, created_at, status, retries',
  meta: 'key', // sync cursor, current user, settings
});

// v2 — Outbox de acciones (motor de plantillas 2-16). Se reenvían a /actions/submit
// cuando vuelve la señal; la lógica de negocio permanece solo en el backend.
db.version(2).stores({
  actions_outbox: '++id, type, created_at, status, retries',
});

export const META = {
  SYNC_CURSOR: 'sync_cursor',
  CURRENT_USER: 'current_user',
  AUTH_TOKEN: 'auth_token',
  READER_MODE: 'reader_mode',
  PRESENT_COLOR: 'present_color',
  DEVICE_ID: 'device_id',
  LAST_FULL_HEAL: 'last_full_heal',
  LAST_OWNER_SCOPE: 'last_owner_scope',
  LAST_RECONCILE_OK: 'last_reconcile_ok',
  STORAGE_PERSISTED: 'storage_persisted',
  LAST_LOCAL_EXPORT_AT: 'last_local_export_at',
  // Fix D (2026-08-24): epoch de datos — sube cada vez que el dueño reinicia
  // el sistema. Si el valor del servidor supera al guardado acá, este
  // dispositivo debe descartar su copia local antes de sincronizar de nuevo.
  DATA_EPOCH: 'data_epoch',
};

// Borra el espejo local completo (tablas + cola de sync + cursor). Se usa
// SOLO cuando cambia el "dueño" de los datos en este dispositivo (login de un
// owner distinto) o cuando el usuario elige explícitamente perder sus datos
// sin subir (escape hatch de logout). Nunca se llama en un logout normal.
export async function wipeLocalMirror() {
  await Promise.all([
    db.animals.clear(), db.farms.clear(), db.lots.clear(),
    db.events.clear(), db.inventories.clear(), db.inventory_scans.clear(),
    db.sync_queue.clear(), db.actions_outbox.clear(),
  ]);
  await setMeta(META.SYNC_CURSOR, null);
  await setMeta(META.LAST_FULL_HEAL, null);
}

export async function getMeta(key, defaultValue = null) {
  const row = await db.meta.get(key);
  return row ? row.value : defaultValue;
}

export async function setMeta(key, value) {
  await db.meta.put({ key, value });
}

export async function getOrCreateDeviceId() {
  let id = await getMeta(META.DEVICE_ID);
  if (!id) {
    id = 'dev-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now();
    await setMeta(META.DEVICE_ID, id);
  }
  return id;
}

export function nowIso() {
  return new Date().toISOString();
}

export function uuid() {
  // RFC4122-ish v4
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
